import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  MagnifyingGlassIcon,
  XCircleIcon,
  CheckIcon,
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import { Link } from 'react-router-dom'
import { Heading, Text, Button, Input, Select, LoadingSpinner, SortableTableHeader } from '../../components/ui'
import { PermissionGuard } from '../../components/PermissionGuard'
import { useAuthContext } from '../../contexts/AuthContext'
import { usePermission } from '../../hooks/usePermissions'
import { useUserLogs } from '../../hooks/useUserLogs'
import { modulesService } from '../../services/modules'
import { marketingService } from '../../services/marketing'
import type {
  MarketingClippingCategory,
  MarketingClippingSearchConfig,
  MarketingClippingSearchSite,
  MarketingClippingVehicle,
  MarketingNewsAlert,
  MarketingNewsAlertFilters,
  MarketingNewsAlertTriageStatus,
  NewsAlertTriageCounts,
} from '../../types/marketing'
import AlertaDetalheModal from './AlertaDetalheModal'
import AlertaPromoverModal from './AlertaPromoverModal'
import AlertasDashboardTab from './AlertasDashboardTab'

const MODULE_NAME = 'marketing_alertas'
const PERM_LOADING: React.ReactNode = null

const TRIAGE_OPTIONS: { value: MarketingNewsAlertTriageStatus; label: string }[] = [
  { value: 'novo', label: 'Novo' },
  { value: 'analisado', label: 'Analisado' },
  { value: 'promovido', label: 'Clipping' },
  { value: 'descartado', label: 'Descartado' },
]

const DEFAULT_TRIAGE: MarketingNewsAlertTriageStatus[] = ['novo', 'analisado']

type AlertSortKey =
  | 'title'
  | 'source'
  | 'published_at'
  | 'triage_status'

function alertSourceLabel(item: MarketingNewsAlert): string {
  return item.search_site?.vehicle?.name || item.search_site?.name || ''
}

function compareNullable(
  a: string | number | null | undefined,
  b: string | number | null | undefined,
  order: 'asc' | 'desc'
): number {
  const aEmpty = a == null || a === ''
  const bEmpty = b == null || b === ''
  if (aEmpty && bEmpty) return 0
  if (aEmpty) return 1
  if (bEmpty) return -1
  if (a < b) return order === 'asc' ? -1 : 1
  if (a > b) return order === 'asc' ? 1 : -1
  return 0
}

type ActiveTab = 'relacao' | 'dashboard'

const tabClass = (activeTab: ActiveTab, tab: ActiveTab) =>
  `py-3 px-1 border-b-2 font-medium text-sm ${
    activeTab === tab
      ? 'border-primary-600 text-primary-600 dark:text-primary-400'
      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
  }`

function formatDateTimeBr(value: string | null | undefined): string {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value.slice(0, 10)
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function triageBadgeClass(status: MarketingNewsAlertTriageStatus): string {
  switch (status) {
    case 'novo':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300'
    case 'analisado':
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
    case 'promovido':
      return 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
    case 'descartado':
      return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
    default:
      return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
  }
}

function triageLabel(status: MarketingNewsAlertTriageStatus): string {
  return TRIAGE_OPTIONS.find((o) => o.value === status)?.label || status
}

const MarketingAlertasPage: React.FC = () => {
  const { user } = useAuthContext()
  const { logCreate, logUpdate, logDelete } = useUserLogs()
  const { hasPermission: canInsert } = usePermission(MODULE_NAME, 'insert')
  const { hasPermission: canUpdate } = usePermission(MODULE_NAME, 'update')
  const { hasPermission: canDelete } = usePermission(MODULE_NAME, 'delete')
  const tableColSpan =
    5 + (canInsert ? 1 : 0) + (canUpdate ? 1 : 0) + (canDelete ? 1 : 0)

  const [moduleTitle, setModuleTitle] = useState('Alertas')
  const [moduleDescription, setModuleDescription] = useState(
    'Fila de triagem das notícias encontradas pela coleta automática'
  )

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actionLoading, setActionLoading] = useState(false)

  const [alerts, setAlerts] = useState<MarketingNewsAlert[]>([])
  const [triageCounts, setTriageCounts] = useState<NewsAlertTriageCounts>({
    novo: 0,
    analisado: 0,
    promovido: 0,
    descartado: 0,
  })
  const [veiculos, setVeiculos] = useState<MarketingClippingVehicle[]>([])
  const [categorias, setCategorias] = useState<MarketingClippingCategory[]>([])
  const [sites, setSites] = useState<MarketingClippingSearchSite[]>([])
  const [searchConfigs, setSearchConfigs] = useState<MarketingClippingSearchConfig[]>([])

  const [filterQuery, setFilterQuery] = useState('')
  const [filterTriage, setFilterTriage] = useState<MarketingNewsAlertTriageStatus[]>(DEFAULT_TRIAGE)
  const [filterIaSuggestion, setFilterIaSuggestion] = useState<'' | 'sim' | 'nao'>('')
  const [filterMinScore, setFilterMinScore] = useState('')
  const [filterDataInicio, setFilterDataInicio] = useState('')
  const [filterDataFim, setFilterDataFim] = useState('')
  const [filterSiteId, setFilterSiteId] = useState('')
  const [filterSearchConfigId, setFilterSearchConfigId] = useState('')

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(20)
  const [activeTab, setActiveTab] = useState<ActiveTab>('relacao')
  const [sortKey, setSortKey] = useState<AlertSortKey>('published_at')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')

  const [promoteAlert, setPromoteAlert] = useState<MarketingNewsAlert | null>(null)
  const [detailAlert, setDetailAlert] = useState<MarketingNewsAlert | null>(null)

  useEffect(() => {
    modulesService.getByName(MODULE_NAME).then((result) => {
      if (result.success && result.data) {
        if (result.data.display_name) setModuleTitle(result.data.display_name)
        if (result.data.description) setModuleDescription(result.data.description)
      }
    })
  }, [])

  const buildFilters = useCallback((): MarketingNewsAlertFilters => {
    const filters: MarketingNewsAlertFilters = {}
    if (filterTriage.length > 0) filters.triage_status = filterTriage
    if (filterIaSuggestion === 'sim') filters.ia_is_clipping = true
    if (filterIaSuggestion === 'nao') filters.ia_is_clipping = false
    if (filterMinScore.trim()) {
      const score = Number(filterMinScore)
      if (!Number.isNaN(score)) filters.min_score = score
    }
    if (filterDataInicio) filters.published_from = `${filterDataInicio}T00:00:00`
    if (filterDataFim) filters.published_to = `${filterDataFim}T23:59:59`
    if (filterSiteId) filters.search_site_id = filterSiteId
    if (filterSearchConfigId) filters.search_config_id = filterSearchConfigId
    if (filterQuery.trim()) filters.search = filterQuery.trim()
    return filters
  }, [
    filterTriage,
    filterIaSuggestion,
    filterMinScore,
    filterDataInicio,
    filterDataFim,
    filterSiteId,
    filterSearchConfigId,
    filterQuery,
  ])

  const loadAll = useCallback(async (opts?: { showOverlay?: boolean }) => {
    const showOverlay = opts?.showOverlay ?? true
    try {
      if (showOverlay) setLoading(true)
      setError(null)
      const [alertsData, counts, veiculosData, categoriasData, sitesData, configsData] =
        await Promise.all([
          marketingService.listNewsAlerts(buildFilters()),
          marketingService.countNewsAlertsByTriage(),
          marketingService.listVehicles(false),
          marketingService.listCategories(false),
          marketingService.listSearchSites(false),
          marketingService.listSearchConfigs(),
        ])
      setAlerts(alertsData)
      setTriageCounts(counts)
      setVeiculos(veiculosData)
      setCategorias(categoriasData)
      setSites(sitesData)
      setSearchConfigs([...configsData].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')))
      setSelectedIds(new Set())
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar alertas')
    } finally {
      if (showOverlay) setLoading(false)
    }
  }, [buildFilters])

  useEffect(() => {
    loadAll()
  }, [loadAll])

  useEffect(() => {
    setCurrentPage(1)
  }, [filterQuery, filterTriage, filterIaSuggestion, filterMinScore, filterDataInicio, filterDataFim, filterSiteId, filterSearchConfigId])

  const hasActiveFilters = Boolean(
    filterQuery.trim() ||
      filterIaSuggestion ||
      filterMinScore.trim() ||
      filterDataInicio ||
      filterDataFim ||
      filterSiteId ||
      filterSearchConfigId ||
      filterTriage.join(',') !== DEFAULT_TRIAGE.join(',')
  )

  const clearFilters = () => {
    setFilterQuery('')
    setFilterTriage(DEFAULT_TRIAGE)
    setFilterIaSuggestion('')
    setFilterMinScore('')
    setFilterDataInicio('')
    setFilterDataFim('')
    setFilterSiteId('')
    setFilterSearchConfigId('')
  }

  const toggleTriageFilter = (status: MarketingNewsAlertTriageStatus) => {
    setFilterTriage((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
    )
  }

  const toggleSort = useCallback((key: string) => {
    const nextKey = key as AlertSortKey
    if (sortKey === nextKey) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(nextKey)
      setSortOrder(nextKey === 'published_at' ? 'desc' : 'asc')
    }
  }, [sortKey])

  const sortedAlerts = useMemo(() => {
    return [...alerts].sort((a, b) => {
      switch (sortKey) {
        case 'title':
          return compareNullable(
            a.title.toLowerCase(),
            b.title.toLowerCase(),
            sortOrder
          )
        case 'source':
          return compareNullable(
            alertSourceLabel(a).toLowerCase(),
            alertSourceLabel(b).toLowerCase(),
            sortOrder
          )
        case 'published_at':
          return compareNullable(
            a.published_at ? new Date(a.published_at).getTime() : null,
            b.published_at ? new Date(b.published_at).getTime() : null,
            sortOrder
          )
        case 'triage_status':
          return compareNullable(a.triage_status, b.triage_status, sortOrder)
        default:
          return 0
      }
    })
  }, [alerts, sortKey, sortOrder])

  const totalPages = Math.max(1, Math.ceil(sortedAlerts.length / itemsPerPage) || 1)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedItems = sortedAlerts.slice(startIndex, endIndex)

  const selectableOnPage = useMemo(
    () => paginatedItems.filter((a) => a.triage_status === 'novo' || a.triage_status === 'analisado'),
    [paginatedItems]
  )

  const allPageSelected =
    selectableOnPage.length > 0 && selectableOnPage.every((a) => selectedIds.has(a.id))

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (allPageSelected) {
        selectableOnPage.forEach((a) => next.delete(a.id))
      } else {
        selectableOnPage.forEach((a) => next.add(a.id))
      }
      return next
    })
  }

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const removeOne = async (alert: MarketingNewsAlert) => {
    if (!window.confirm(`Excluir o alerta "${alert.title}"?`)) return
    try {
      setActionLoading(true)
      setError(null)
      await marketingService.deleteNewsAlert(alert.id, user?.id ?? null)
      void logDelete(
        'marketing_news_alert',
        alert.id,
        `Excluiu alerta: ${alert.title}`,
        {
          title: alert.title,
          url: alert.url,
          triage_status: alert.triage_status,
          additional_info: 'exclusao_logica',
        }
      )
      if (detailAlert?.id === alert.id) setDetailAlert(null)
      await loadAll({ showOverlay: false })
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir alerta')
    } finally {
      setActionLoading(false)
    }
  }

  const discardOne = async (alert: MarketingNewsAlert) => {
    if (!window.confirm(`Descartar o alerta "${alert.title}"?`)) return
    try {
      setActionLoading(true)
      setError(null)
      const updated = await marketingService.discardNewsAlert(alert.id, user?.id ?? null)
      void logUpdate(
        'marketing_news_alert',
        alert.id,
        `Descartou alerta: ${alert.title}`,
        { triage_status: alert.triage_status },
        { triage_status: updated.triage_status }
      )
      await loadAll({ showOverlay: false })
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao descartar alerta')
    } finally {
      setActionLoading(false)
    }
  }

  const discardSelected = async () => {
    const ids = Array.from(selectedIds)
    if (ids.length === 0) return
    if (!window.confirm(`Descartar ${ids.length} alerta(s) selecionado(s)?`)) return
    try {
      setActionLoading(true)
      setError(null)
      await marketingService.discardNewsAlerts(ids, user?.id ?? null)
      void logUpdate(
        'marketing_news_alert',
        null,
        `Descartou ${ids.length} alerta(s) em lote`,
        undefined,
        { ids }
      )
      await loadAll({ showOverlay: false })
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao descartar alertas')
    } finally {
      setActionLoading(false)
    }
  }

  const reopenOne = async (alert: MarketingNewsAlert) => {
    try {
      setActionLoading(true)
      setError(null)
      const updated = await marketingService.reopenNewsAlert(alert.id)
      void logUpdate(
        'marketing_news_alert',
        alert.id,
        `Reabriu alerta: ${alert.title}`,
        { triage_status: alert.triage_status },
        { triage_status: updated.triage_status }
      )
      await loadAll({ showOverlay: false })
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao reabrir alerta')
    } finally {
      setActionLoading(false)
    }
  }

  const handleCatalogCreated = (
    kind: 'veiculo' | 'categoria',
    item: MarketingClippingVehicle | MarketingClippingCategory
  ) => {
    if (kind === 'veiculo') {
      setVeiculos((prev) =>
        [...prev.filter((v) => v.id !== item.id), item as MarketingClippingVehicle].sort((a, b) =>
          a.name.localeCompare(b.name, 'pt-BR')
        )
      )
    } else {
      setCategorias((prev) =>
        [...prev.filter((c) => c.id !== item.id), item as MarketingClippingCategory].sort((a, b) =>
          a.name.localeCompare(b.name, 'pt-BR')
        )
      )
    }
  }

  return (
    <div className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto">
        <PermissionGuard
          moduleName={MODULE_NAME}
          permissionType="show"
          loadingComponent={PERM_LOADING}
        >
          <div className="py-6 px-4 sm:px-6 lg:px-8">
            <div className="mb-6 flex items-start justify-between gap-4">
              <div>
                <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
                  {moduleTitle}
                </Heading>
                <Text size="sm" color="muted" className="mt-2">
                  {moduleDescription}
                </Text>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  variant="outline"
                  onClick={() => loadAll()}
                  className="flex items-center gap-1"
                  disabled={loading || actionLoading}
                >
                  <ArrowPathIcon className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
                  Atualizar
                </Button>
              </div>
            </div>

            {error && (
              <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-700 dark:text-red-400">
                {error}
              </div>
            )}

            <div className="mb-4 bg-white dark:bg-gray-800 rounded-lg shadow p-4">
              <div className="mb-4">
                <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                  Filtros
                </Heading>
              </div>

              <div className="mb-4">
                <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                  Status de triagem
                </Text>
                <div className="flex flex-wrap gap-2">
                  {TRIAGE_OPTIONS.map((opt) => {
                    const active = filterTriage.includes(opt.value)
                    const count = triageCounts[opt.value]
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => toggleTriageFilter(opt.value)}
                        className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm border transition-colors ${
                          active
                            ? 'border-primary-600 bg-primary-50 text-primary-800 dark:bg-primary-900/30 dark:text-primary-200 dark:border-primary-500'
                            : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-300 dark:hover:bg-gray-700'
                        }`}
                      >
                        {opt.label}
                        <span className="tabular-nums text-xs text-gray-500 dark:text-gray-400">
                          ({count})
                        </span>
                      </button>
                    )
                  })}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="min-w-0">
                  <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                    Buscar
                  </Text>
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      className="pl-9"
                      placeholder="Título ou resumo..."
                      value={filterQuery}
                      onChange={(e) => setFilterQuery(e.target.value)}
                    />
                  </div>
                </div>

                <div className="min-w-0">
                  <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                    Sugestão da IA
                  </Text>
                  <Select
                    value={filterIaSuggestion}
                    onChange={(e) => setFilterIaSuggestion(e.target.value as '' | 'sim' | 'nao')}
                  >
                    <option value="">Todas</option>
                    <option value="sim">É clipping</option>
                    <option value="nao">Não é clipping</option>
                  </Select>
                </div>

                <div className="min-w-0">
                  <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                    Score mínimo
                  </Text>
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    value={filterMinScore}
                    onChange={(e) => setFilterMinScore(e.target.value)}
                    placeholder="0–100"
                  />
                </div>

                <div className="min-w-0">
                  <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                    Site
                  </Text>
                  <Select
                    value={filterSiteId}
                    onChange={(e) => setFilterSiteId(e.target.value)}
                  >
                    <option value="">Todos</option>
                    {sites.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </Select>
                </div>

                <div className="min-w-0">
                  <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block whitespace-nowrap">
                    Configuração de Busca
                  </Text>
                  <Select
                    value={filterSearchConfigId}
                    onChange={(e) => setFilterSearchConfigId(e.target.value)}
                  >
                    <option value="">Todas</option>
                    {searchConfigs.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </Select>
                </div>

                <div className="min-w-0">
                  <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                    Data inicial
                  </Text>
                  <Input
                    type="date"
                    value={filterDataInicio}
                    onChange={(e) => setFilterDataInicio(e.target.value)}
                  />
                </div>

                <div className="min-w-0">
                  <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                    Data final
                  </Text>
                  <Input
                    type="date"
                    value={filterDataFim}
                    onChange={(e) => setFilterDataFim(e.target.value)}
                  />
                </div>

                <div className="min-w-0 flex items-end">
                  <Button
                    variant="outline"
                    onClick={clearFilters}
                    disabled={!hasActiveFilters}
                    className="flex items-center gap-1 whitespace-nowrap"
                  >
                    <XCircleIcon className="h-4 w-4" />
                    Limpar filtros
                  </Button>
                </div>
              </div>

              <div className="mt-4">
                <Text size="sm" color="muted">
                  {alerts.length} registro{alerts.length === 1 ? '' : 's'}
                  {hasActiveFilters ? ' com os filtros aplicados' : ''}
                </Text>
              </div>
            </div>

            <div className="mb-4 border-b border-gray-200 dark:border-gray-700">
              <nav className="-mb-px flex space-x-8">
                <button
                  type="button"
                  onClick={() => setActiveTab('relacao')}
                  className={tabClass(activeTab, 'relacao')}
                >
                  Relação completa
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('dashboard')}
                  className={tabClass(activeTab, 'dashboard')}
                >
                  Dashboard
                </button>
              </nav>
            </div>

            {activeTab === 'dashboard' && (
              <AlertasDashboardTab alerts={alerts} loading={loading} />
            )}

            {activeTab === 'relacao' && (
              <>
            {selectedIds.size > 0 && (
              <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                <Text size="sm" className="text-gray-700 dark:text-gray-300">
                  {selectedIds.size} alerta{selectedIds.size === 1 ? '' : 's'} selecionado
                  {selectedIds.size === 1 ? '' : 's'}
                </Text>
                {canUpdate && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={discardSelected}
                    disabled={actionLoading}
                  >
                    Descartar selecionados ({selectedIds.size})
                  </Button>
                )}
              </div>
            )}

            <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900/30">
                    <tr>
                      <th className="px-3 py-3 w-10">
                        <input
                          type="checkbox"
                          checked={allPageSelected}
                          onChange={toggleSelectAll}
                          disabled={selectableOnPage.length === 0}
                          className="rounded border-gray-300 dark:border-gray-600"
                          aria-label="Selecionar todos da página"
                        />
                      </th>
                      <SortableTableHeader
                        sortKey="title"
                        currentSort={sortKey}
                        sortOrder={sortOrder}
                        onSort={toggleSort}
                        className="px-4 py-3"
                        compact
                      >
                        Título
                      </SortableTableHeader>
                      <SortableTableHeader
                        sortKey="source"
                        currentSort={sortKey}
                        sortOrder={sortOrder}
                        onSort={toggleSort}
                        className="px-4 py-3"
                        compact
                      >
                        Fonte
                      </SortableTableHeader>
                      <SortableTableHeader
                        sortKey="published_at"
                        currentSort={sortKey}
                        sortOrder={sortOrder}
                        onSort={toggleSort}
                        className="px-4 py-3"
                        compact
                      >
                        Publicado
                      </SortableTableHeader>
                      <SortableTableHeader
                        sortKey="triage_status"
                        currentSort={sortKey}
                        sortOrder={sortOrder}
                        onSort={toggleSort}
                        className="px-4 py-3"
                        compact
                      >
                        Status
                      </SortableTableHeader>
                      {canInsert && (
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                          Clipping
                        </th>
                      )}
                      {canUpdate && (
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                          Triagem
                        </th>
                      )}
                      {canDelete && (
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                          Excluir
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {loading ? (
                      <tr>
                        <td colSpan={tableColSpan} className="px-4 py-10 text-center">
                          <LoadingSpinner />
                        </td>
                      </tr>
                    ) : alerts.length === 0 ? (
                      <tr>
                        <td colSpan={tableColSpan} className="px-4 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                          {hasActiveFilters
                            ? 'Nenhum alerta encontrado com os filtros aplicados.'
                            : 'Nenhum alerta encontrado.'}
                        </td>
                      </tr>
                    ) : (
                      paginatedItems.map((item) => {
                        const canSelect =
                          item.triage_status === 'novo' || item.triage_status === 'analisado'
                        const sourceLabel =
                          item.search_site?.vehicle?.name ||
                          item.search_site?.name ||
                          '—'
                        return (
                          <tr
                            key={item.id}
                            className="hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer"
                            onClick={() => setDetailAlert(item)}
                          >
                            <td
                              className="px-3 py-3"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <input
                                type="checkbox"
                                checked={selectedIds.has(item.id)}
                                onChange={() => toggleSelect(item.id)}
                                disabled={!canSelect}
                                className="rounded border-gray-300 dark:border-gray-600"
                                aria-label={`Selecionar ${item.title}`}
                              />
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <div className="flex items-start gap-1">
                                <span
                                  className="line-clamp-2 font-medium text-gray-900 dark:text-white"
                                  title={item.title}
                                >
                                  {item.title.length > 60 ? `${item.title.slice(0, 60)}…` : item.title}
                                </span>
                                {item.url ? (
                                  <a
                                    href={item.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 shrink-0 mt-0.5"
                                    title="Abrir matéria"
                                    aria-label="Abrir matéria em nova aba"
                                  >
                                    <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                                  </a>
                                ) : null}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                              {sourceLabel}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                              {formatDateTimeBr(item.published_at)}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <span
                                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${triageBadgeClass(item.triage_status)}`}
                              >
                                {triageLabel(item.triage_status)}
                              </span>
                            </td>
                            {canInsert && (
                              <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                                {item.triage_status === 'promovido' && item.clipping_id ? (
                                  <Link
                                    to="/marketing/clipping"
                                    className="inline-flex items-center text-sm text-primary-600 dark:text-primary-400 hover:underline"
                                  >
                                    Ver clipping
                                  </Link>
                                ) : item.triage_status === 'novo' || item.triage_status === 'analisado' ? (
                                  <Button
                                    variant="solid"
                                    size="sm"
                                    onClick={() => setPromoteAlert(item)}
                                    disabled={actionLoading}
                                    title="Clipping"
                                    aria-label="Clipping"
                                    className="inline-flex items-center justify-center shrink-0 !bg-primary-600 !text-white ring-0 hover:!bg-primary-700 focus:ring-primary-500/25 dark:!bg-primary-600 dark:!text-white dark:hover:!bg-primary-500"
                                  >
                                    <CheckIcon className="h-4 w-4" />
                                  </Button>
                                ) : null}
                              </td>
                            )}
                            {canUpdate && (
                              <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                                {item.triage_status === 'novo' || item.triage_status === 'analisado' ? (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => discardOne(item)}
                                    disabled={actionLoading}
                                    className="shrink-0"
                                  >
                                    Descartar
                                  </Button>
                                ) : item.triage_status === 'descartado' ? (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => reopenOne(item)}
                                    disabled={actionLoading}
                                    className="shrink-0"
                                  >
                                    Reabrir
                                  </Button>
                                ) : null}
                              </td>
                            )}
                            {canDelete && (
                              <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeOne(item)}
                                  disabled={actionLoading}
                                  title="Excluir"
                                  className="shrink-0"
                                >
                                  <TrashIcon className="h-4 w-4 text-red-500" />
                                </Button>
                              </td>
                            )}
                          </tr>
                        )
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {!loading && alerts.length > 0 && (
                <div className="px-4 sm:px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <Text size="sm" className="text-gray-700 dark:text-gray-300">
                      Mostrando {startIndex + 1} a {Math.min(endIndex, alerts.length)} de{' '}
                      {alerts.length} registros
                    </Text>
                    {totalPages > 1 && (
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                        >
                          Anterior
                        </Button>
                        <Text size="sm" className="text-gray-600 dark:text-gray-300">
                          Página {currentPage} de {totalPages}
                        </Text>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                          disabled={currentPage === totalPages}
                        >
                          Próxima
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
              </>
            )}
          </div>
        </PermissionGuard>

      {detailAlert && (
        <AlertaDetalheModal alert={detailAlert} onClose={() => setDetailAlert(null)} />
      )}

      {promoteAlert && (
        <AlertaPromoverModal
          alert={promoteAlert}
          veiculos={veiculos}
          categorias={categorias}
          userId={user?.id}
          onClose={() => setPromoteAlert(null)}
          onCatalogCreated={handleCatalogCreated}
          onPromoted={({ clippingId, alert }) => {
            void logCreate(
              'marketing_news_alert',
              alert.id,
              `Marcou alerta como clipping: ${alert.title}`,
              { clipping_id: clippingId, triage_status: 'promovido' }
            )
            setPromoteAlert(null)
            void loadAll({ showOverlay: false })
          }}
        />
      )}
    </div>
  )
}

export default MarketingAlertasPage
